/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FormBox.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Modern form box component for organizing form sections with a clean, tab-like header design.

 🔧 Notes:
    - Replaces the basic boxes used in forms with a modern design
    - Provides consistent styling across all form modules
    - Supports icons, descriptions, and collapsible sections

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

export interface FormBoxAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'default' | 'outline' | 'ghost';
}

interface FormBoxProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  variant?: 'default' | 'elevated' | 'outlined';
  actions?: FormBoxAction[];
  headerContent?: React.ReactNode;
  isLoading?: boolean;
}

export function FormBox({
  title,
  description,
  icon,
  children,
  className,
  collapsible = false,
  defaultCollapsed = false,
  variant = 'default',
  actions = [],
  headerContent,
  isLoading = false
}: FormBoxProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const handleToggle = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: FormBoxAction) => {
    e.stopPropagation(); // Prevent collapsing when clicking action buttons
    action.onClick();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return 'bg-card border border-border shadow-lg';
      case 'outlined':
        return 'bg-transparent border-2 border-border';
      default:
        return 'bg-card border border-border';
    }
  };

  return (
    <div className={cn(
      "rounded-lg transition-all duration-200",
      getVariantStyles(),
      className
    )}>
      {/* Folder Tab Header */}
      <div
        className={cn(
          "relative py-2 border-b border-border/50",
          "bg-gradient-to-r from-muted/50 via-muted/30 via-muted/10 to-transparent",
          collapsible && "cursor-pointer hover:from-muted/70 hover:via-muted/40 hover:via-muted/15 transition-all duration-300"
        )}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            {icon && (
              <div className="flex-shrink-0 text-primary">
                {icon}
              </div>
            )}
            <h3 className="text-lg font-semibold text-foreground">
              {title}
            </h3>
          </div>

          <div className="flex items-center gap-2">
            {/* Action Buttons */}
            {actions.length > 0 && (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    type="button"
                    variant={action.variant || 'outline'}
                    size="sm"
                    onClick={(e) => handleActionClick(e, action)}
                    className="flex items-center gap-2"
                  >
                    {action.icon}
                    {action.label}
                  </Button>
                ))}
              </div>
            )}

            {/* Custom Header Content */}
            {headerContent && (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {headerContent}
              </div>
            )}

            {/* Collapse Icon */}
            {collapsible && (
              <div className="flex-shrink-0 text-muted-foreground">
                {isCollapsed ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div
          className={cn(
            'p-3',
            isLoading && 'pointer-events-none opacity-60 transition-opacity'
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// Compact version for smaller forms
export function CompactFormBox({
  title,
  description,
  icon,
  children,
  className,
  collapsible = false,
  defaultCollapsed = false
}: FormBoxProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const handleToggle = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div className={cn(
      "bg-card border border-border rounded-lg transition-all duration-200",
      className
    )}>
      {/* Compact Folder Tab Header */}
      <div
        className={cn(
          "relative py-1.5 border-b border-border/50",
          "bg-gradient-to-r from-muted/50 via-muted/30 via-muted/10 to-transparent",
          collapsible && "cursor-pointer hover:from-muted/70 hover:via-muted/40 hover:via-muted/15 transition-all duration-300"
        )}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            {icon && (
              <div className="flex-shrink-0 text-primary">
                {icon}
              </div>
            )}
            <h3 className="text-base font-semibold text-foreground">
              {title}
            </h3>
          </div>

          {collapsible && (
            <div className="flex-shrink-0 text-muted-foreground">
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-4">
          {children}
        </div>
      )}
    </div>
  );
}
